/**
 * RentalData
 * --
 */
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { Content, Title, ModalLayout } from 'components';
import {
  Button,
  Input,
  Select,
  Upload,
  Spin,
  Tag,
  Table,
  Space,
  Divider,
  Tabs,
  Descriptions,
  Empty,
  Flex,
  DatePicker,
} from 'antd';

import MessageAlert, { stringToMoneyFormat, strCut, isNull } from 'utils';
import trackDelivery from 'utils/TrackDelivery';
import moment from 'moment';
import {
  FormOutlined,
  UploadOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import DeliveryCompany from 'constants/DeliveryCompany';
import RentalDetail from './RentalDetail';
import {
  PAYMENT_METHOD_CONSTANT, // 주문 유형
  DELIVERY_STATUS,
  PAYMENT_STATUS_CONSTANT,
  RENTAL_TYPE_CONSTANT,
} from 'constants/ProductConstants';
import { PURCHASE_STATUS } from 'constants/OrderConstants';
import * as XLSX from 'xlsx/xlsx.mjs';

import { useBulkUpdateRental, useUpdateStatusRental } from 'hooks/RentalHooks';

const initialRegisterDeliveryInfo = {
  delivery_company: '',
  invoice_number: '',
};

/**----------------------------------------
 * [Component] 필터
 * ----------------------------------------
 */
const RentalData = ({
  type,
  orderList,
  onSaveDeliveryInfo,
  onReload,
  onCancel,
}) => {
  /* ===== ROUTE ===== */

  /* ===== MUTATE ===== */
  const { bulkUpdateRental } = useBulkUpdateRental();
  const { updateStatusRental } = useUpdateStatusRental();
  /* ===== PROPS ===== */
  /* ===== STATE ===== */
  // 로딩
  const [loading, setLoading] = useState(false);
  // 선택항목
  const [selectedList, setSelectedList] = useState([]);

  // 상세보기 모달
  const [detailModal, setDetailModal] = useState(null);
  const [dateModal, setDateModal] = useState(null);
  // 택배정보 등록 모달
  const [registerDeliveryInfoModal, setRegisterDeliveryInfoModal] =
    useState(null);
  // 택배정보 입력 데이터
  const [registerDeliveryInfo, setRegisterDeliveryInfo] = useState(
    initialRegisterDeliveryInfo,
  );
  // 택배정보 입력 데이터
  const [currentDeliveryCompany, setCurrentDeliveryCompany] = useState(1);
  // 탭
  const [currentTab, setCurrentTab] = useState(1);
  // 택배정보 입력 데이터
  const [uploadData, setUploadData] = useState(null);

  const searchInput = React.useRef(null);
  /* ===== VARIABLE ===== */
  const handleTableSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
  };
  const handleTableReset = (clearFilters, confirm) => {
    clearFilters();
    confirm();
  };
  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div
        style={{
          padding: 8,
        }}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <Input
          ref={searchInput}
          placeholder={`검색어를 입력해주세요`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() =>
            handleTableSearch(selectedKeys, confirm, dataIndex)
          }
          style={{
            marginBottom: 8,
            display: 'block',
          }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleTableSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{
              width: 90,
            }}
          >
            검색
          </Button>
          <Button
            onClick={() =>
              clearFilters && handleTableReset(clearFilters, confirm)
            }
            size="small"
            style={{
              width: 90,
            }}
          >
            초기화
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined
        style={{
          color: filtered ? '#1677ff' : undefined,
        }}
      />
    ),
    onFilter: (value, record) => {
      const [main, sub, third] = dataIndex?.split('.');
      const dataValue = third
        ? record[main][sub][third]
        : sub
          ? record[main][sub]
          : record[main];
      return dataValue
        ?.toString()
        ?.toLowerCase()
        ?.includes(value.toLowerCase());
    },
    filterDropdownProps: {
      onOpenChange(open) {
        if (open) {
          setTimeout(() => searchInput.current?.select(), 100);
        }
      },
    },
    render: (text) => text,
  });

  // 택배사 정보
  const deliveryCompanyList = Object.keys(DeliveryCompany).slice(0, 20);

  const columns = [
    {
      title: '택배사/송장번호',
      key: 'delivery_company',
      dataIndex: 'delivery_company',
      width: 145,
      align: 'center',
      fixed: 'left',
      render: (v, row) =>
        row?.type !== 'STORE_PICKUP' ? (
          row.invoice_number ? (
            <>
              <div>
                <span
                  style={{
                    fontSize: '0.88em',
                    letterSpacing: -0.8,
                  }}
                >{`${v ?? ''} - ${row.invoice_number ?? ''}`}</span>
              </div>
              <Button
                size="small"
                type="default"
                onClick={() => setRegisterDeliveryInfoModal(row)}
              >
                <FormOutlined style={{ fontSize: '0.95em' }} /> 수정
              </Button>
            </>
          ) : (
            <>
              <Button
                type="primary"
                onClick={() => setRegisterDeliveryInfoModal(row)}
              >
                택배등록
              </Button>
            </>
          )
        ) : (
          <Button
            type="primary"
            // onClick={() => setRegisterDeliveryInfoModal(row)}
          >
            스토어픽업
          </Button>
        ),
    },
    {
      title: (
        <>
          주문일시
          <br />
          (신청일시)
        </>
      ),
      key: 'created_at',
      dataIndex: 'created_at',
      width: 160,
      align: 'center',
      render: (val) => moment(val * 1000).format('YYYY-MM-DD(HH:mm:ss)'),
      sorter: (a, b) => a.created_at - b.created_at,
    },

    {
      title: '주문번호',
      key: 'shop_order_id',
      dataIndex: 'shop_order_id',
      align: 'center',
      width: 100,
      sorter: (a, b) => a.shop_order_id - b.shop_order_id,
    },
    {
      title: '품목별주문번호',
      key: 'rental_id',
      dataIndex: 'rental_id',
      align: 'center',
      width: 120,
      sorter: (a, b) => a.rental_id - b.rental_id,
    },
    {
      title: '진행회차',
      key: 'rental_payment_info',
      dataIndex: 'rental_payment_info',
      align: 'center',
      width: 120,
      render: (rental_payment_info) => rental_payment_info?.length + '회차',
      sorter: (a, b) =>
        a.rental_payment_info?.length - b.rental_payment_info?.length,
    },
    {
      title: '판매자',
      key: 'shop_information',
      dataIndex: 'shop_information',
      width: 170,
      align: 'center',
      ...getColumnSearchProps('shop_information.shop_information_name'),
      render: (record) => record?.shop_information_name ?? '',
    },
    {
      title: '구매자',
      key: 'user',
      dataIndex: 'user',
      width: 110,
      align: 'center',
      ...getColumnSearchProps('user.user_name'),
      render: (val) => val?.user_name ?? '',
    },
    {
      title: '상품명',
      key: 'product',
      dataIndex: 'product',
      width: 180,
      align: 'center',
      ...getColumnSearchProps('product.product_name'),
      render: (product) => product.product_name,
    },
    {
      title: '상품옵션정보',
      key: 'product',
      dataIndex: 'product',
      width: 180,
      align: 'center',
      render: (product) => (
        <ul style={{ fontSize: '0.9em' }}>
          {product?.product_option?.length
            ? product?.product_option?.map((item) => (
                <li>
                  - {item.option_title} /{' '}
                  {item?.option?.map((option) => (
                    <>{option?.option_name || '-'}</>
                  ))}
                </li>
              ))
            : '-'}
        </ul>
      ),
    },
    {
      title: '렌탈선택옵션',
      key: 'rental_option',
      dataIndex: 'rental_option',
      width: 400,
      align: 'center',
      render: (rental_option) => {
        const { contract_date, advance_price, management, additional_cost } =
          rental_option;
        return (
          <ul style={{ fontSize: '1.0em' }}>
            {
              <ul>
                <li>
                  - 선납입금 : {advance_price?.option_name} /{' '}
                  {RENTAL_TYPE_CONSTANT[advance_price?.option_type]?.title} /{' '}
                  {stringToMoneyFormat(advance_price?.option_price, '원')}
                </li>
                <li>
                  - 계약기간 : {contract_date?.option_name} /{' '}
                  {contract_date?.option_name} 개월 /{' '}
                  {RENTAL_TYPE_CONSTANT[contract_date?.option_type]?.title} /{' '}
                  {stringToMoneyFormat(contract_date?.option_price, '원')}
                </li>
                <li>
                  - 의무사용기간 :
                  {contract_date?.mandatory_usage_date?.option_name} /{' '}
                  {contract_date?.mandatory_usage_date?.option_name} 개월 /{' '}
                  {
                    RENTAL_TYPE_CONSTANT[
                      contract_date?.mandatory_usage_date?.option_type
                    ]?.title
                  }
                  /{' '}
                  {stringToMoneyFormat(
                    contract_date?.mandatory_usage_date?.option_price,
                    '원',
                  )}
                </li>
                <li>
                  - 관리유형 : {management?.option_name} /{' '}
                  {RENTAL_TYPE_CONSTANT[management?.option_type]?.title} /{' '}
                  {stringToMoneyFormat(management?.option_price, '원')}
                </li>
                <li>
                  - 추가비용 : {additional_cost?.option_name} /{' '}
                  {RENTAL_TYPE_CONSTANT[additional_cost?.option_type]?.title} /{' '}
                  {stringToMoneyFormat(additional_cost?.option_price, '원')}
                </li>
              </ul>
            }
          </ul>
        );
      },
    },
    {
      title: '수량',
      key: 'num',
      dataIndex: 'num',
      width: 80,
      align: 'center',
      render: (val) => `${val ?? 0} 개`,
      sorter: (a, b) => a.num - b.num,
    },
    {
      title: '월 결제 금액',
      key: 'rental_price',
      dataIndex: 'rental_price',
      align: 'center',
      width: 130,
      render: (rental_price) => stringToMoneyFormat(rental_price, '원'),
      sorter: (a, b) => a.rental_price - b.rental_price,
    },

    {
      title: '계약기간',
      key: 'contract_date',
      dataIndex: 'contract_date',
      align: 'center',
      width: 130,
      render: (contract_date) => `${contract_date} 개월`,
      sorter: (a, b) => a.contract_date - b.contract_date,
    },
    {
      title: '의무계약기간',
      key: 'mandatory_usage_date',
      dataIndex: 'mandatory_usage_date',
      align: 'center',
      width: 130,
      render: (mandatory_usage_date) => `${mandatory_usage_date} 개월`,
      sorter: (a, b) => a.mandatory_usage_date - b.mandatory_usage_date,
    },

    {
      title: '렌탈시작기간',
      key: 'rental_start_date',
      dataIndex: 'rental_start_date',
      align: 'center',
      width: 150,
      render: (v, record) =>
        v ? (
          <Button
            onClick={() => {
              setDateModal({
                rental_id: record.rental_id,
                title: '렌탈시작기간',
                key: 'rental_start_date',
                value: v,
              });
            }}
          >
            {moment(v * 1000).format('YYYY-MM-DD (HH:mm:ss)')}
          </Button>
        ) : (
          '진행전'
        ),

      sorter: (a, b) => a.rental_start_date - b.rental_start_date,
    },
    {
      title: '렌탈종료기간',
      key: 'rental_end_date',
      dataIndex: 'rental_end_date',
      align: 'center',
      width: 150,
      render: (v, record) =>
        v ? (
          <Button
            onClick={() => {
              setDateModal({
                rental_id: record.rental_id,
                title: '렌탈종료기간',
                key: 'rental_end_date',
                value: v,
              });
            }}
          >
            {moment(v * 1000).format('YYYY-MM-DD (HH:mm:ss)')}
          </Button>
        ) : (
          '진행전'
        ),
      sorter: (a, b) => a.rental_end_date - b.rental_end_date,
    },
    {
      title: '다음결제일',
      key: 'rental_updated_at',
      dataIndex: 'rental_updated_at',
      align: 'center',
      width: 150,
      render: (v, record) =>
        v ? (
          <Button
            onClick={() => {
              setDateModal({
                rental_id: record.rental_id,
                title: '다음결제일',
                key: 'rental_updated_at',
                value: v,
              });
            }}
          >
            {moment(v * 1000).format('YYYY-MM-DD')}
          </Button>
        ) : (
          '-'
        ),
      sorter: (a, b) => a.rental_updated_at - b.rental_updated_at,
    },
    {
      title: '진행상태',
      key: 'is_active',
      dataIndex: 'is_active',
      align: 'center',
      width: 100,
      render: (val, record) => (
        <Select
          style={{ width: 90 }}
          options={[
            { value: 'Y', label: '진행중' },
            { value: 'N', label: '종료' },
          ]}
          onChange={(e) => {
            handleBulkUpdateRental(false, {
              rental_id: record.rental_id,
              title: '진행상태',
              key: 'is_active',
              value: e,
            });
          }}
          defaultValue={val}
        />
      ),
      filters: [
        { text: '진행중', value: 'Y' },
        { text: '종료', value: 'N' },
      ],
      onFilter: (val, record) => record.is_active === val,
    },
    {
      title: '주문상태',
      key: 'purchase_status',
      dataIndex: 'purchase_status',
      align: 'center',
      width: 100,
      render: (val) => (
        <Tag color={PURCHASE_STATUS[val]?.color}>
          {PURCHASE_STATUS[val]?.title}
        </Tag>
      ),
      filters: Object.entries(PURCHASE_STATUS).map(([key, v]) => ({
        text: v.title,
        value: key,
      })),
      onFilter: (val, record) => record.purchase_status === val,
    },
    {
      title: '배송상태',
      key: 'delivery_status',
      dataIndex: 'delivery_status',
      align: 'center',
      width: 100,
      render: (val) => (
        <Tag color={DELIVERY_STATUS[val]?.color}>
          {DELIVERY_STATUS[val]?.title}
        </Tag>
      ),
      filters: Object.entries(DELIVERY_STATUS).map(([key, v]) => ({
        text: v.title,
        value: key,
      })),
      onFilter: (val, record) => record.delivery_status === val,
    },
    {
      title: '배송메모',
      key: 'shop_order',
      dataIndex: 'shop_order',
      width: 240,
      align: 'center',
      render: (record) => strCut(record?.delivery_address?.request, 18),
    },
    ...(type?.includes('RETURN') || type?.includes('EXCHANGE')
      ? [
          {
            title: '환불/반품 사유',
            key: 'refund',
            dataIndex: 'refund',
            width: 170,
            align: 'center',
            render: (item) => strCut(item?.[0]?.refund_reason, 18),
          },
        ]
      : []),

    {
      title: '#',
      align: 'center',
      width: 70,
      fixed: 'right',
      render: (row) => (
        <>
          <Button
            block
            size="small"
            style={{ marginBottom: 2.5 }}
            onClick={() => setDetailModal(row)}
          >
            상세보기
          </Button>
          <Button
            block
            size="small"
            disabled={!row.invoice_number ? true : false}
            onClick={() =>
              trackDelivery(row?.delivery_company, row?.invoice_number)
            }
          >
            배송조회
          </Button>
        </>
      ),
    },
  ];

  const expandableColumns = [
    {
      title: '회차',
      dataIndex: 'rental_payment_id',
      width: 80,
      render: (v) => `${stringToMoneyFormat(Number(v))}회차`,
      sorter: (a, b) => a.rental_payment_id - b.rental_payment_id,
    },
    {
      title: '결제일',
      dataIndex: 'payment_date',
      width: 120,
      render: (v) => (v ? moment(v * 1000).format('YYYY-MM-DD') : '-'),
      sorter: (a, b) => a.payment_date - b.payment_date,
    },
    {
      title: '결제유형',
      dataIndex: 'payment_method',
      width: 100,
      render: (val) => (
        <Tag color={PAYMENT_METHOD_CONSTANT[val]?.color}>
          {PAYMENT_METHOD_CONSTANT[val]?.title}
        </Tag>
      ),
      filters: Object.entries(PAYMENT_METHOD_CONSTANT).map(([key, v]) => ({
        text: v.title,
        value: key,
      })),
      onFilter: (val, record) => record.payment_method === val,
    },
    {
      title: '결제상태',
      dataIndex: 'payment_state',
      width: 100,
      render: (val) => (
        <Tag color={PAYMENT_STATUS_CONSTANT[val]?.color}>
          {PAYMENT_STATUS_CONSTANT[val]?.title}
        </Tag>
      ),
      filters: Object.entries(PAYMENT_STATUS_CONSTANT).map(([key, v]) => ({
        text: v.title,
        value: key,
      })),
      onFilter: (val, record) => record.payment_state === val,
    },
    {
      title: '결제금액',
      dataIndex: 'price',
      width: 120,
      render: (v) => stringToMoneyFormat(Number(v), '원'),
      sorter: (a, b) => a.price - b.price,
    },
  ];
  /* ===== FUNCTION ===== */
  /**
   *
   * --
   */
  const handleSaveDeliveryInfo = async () => {
    const { delivery_company, invoice_number } = registerDeliveryInfo;
    if (!delivery_company || !invoice_number) {
      return MessageAlert.warning('택배정보를 입력해주세요');
    }
    await onSaveDeliveryInfo(
      {
        rental_id: registerDeliveryInfoModal.rental_id,
        ...registerDeliveryInfo,
      },
      () => {
        setRegisterDeliveryInfoModal(null);
        setRegisterDeliveryInfo(initialRegisterDeliveryInfo);
      },
    );
  };

  /**--------------------
   * 한진택배 운송장 - 엑셀 다운로드
   * --------------------
   */
  const handleExportDeliveryData = () => {
    setLoading(true);
    try {
      const xlsxData = orderList.map((item, idx) => {
        const { product, shop_order, num } = item;
        const { product_name } = product;
        const { delivery_address } = shop_order;
        return {
          ['받으시는 분']: delivery_address.recipient_name,
          ['받으시는 분 전화']: delivery_address.recipient_phone,
          [' ']: `    `,
          ['받는 분 핸드폰']: delivery_address.recipient_phone,
          ['  ']: `    `,
          ['받는 분 주소']: `(${delivery_address.recipient_postcode}) ${delivery_address.recipient_address}, ${delivery_address.recipient_address_detail}`,
          ['내품수량']: num,
          ['내품명']: product_name,
          ['   ']: `    `,
          ['    ']: `    `,
          ['     ']: `    `,
          ['특기사항']: delivery_address.request,
        };
      });

      const ws = XLSX.utils.json_to_sheet(xlsxData);
      const wb = XLSX.utils.book_new();

      XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
      setTimeout(() => {
        XLSX.writeFile(
          wb,
          `택배정보 내보내기_${moment().format('YYYYMMDDHHmmss')}.xlsx`,
        );
        setLoading(false);
      }, 1200);
    } catch (e) {
      setLoading(false);
      console.log(e);
      MessageAlert.error('엑셀을 다운로드 할 수 없습니다.');
    }
  };

  /**--------------------
   * 데이터 엑셀 다운로드
   * --------------------
   */
  const handleExportOrderData = (data) => {
    if (
      isNull(data) ||
      !window.confirm(
        '데이터를 엑셀로 다운로드합니다.\n데이터가 많을 경우 다소 시간이 소요됩니다.',
      )
    ) {
      return;
    }
    setLoading(true);
    try {
      const xlsxData = data.map((item) => {
        const { delivery_address } = item?.shop_order;
        const rental_option = item?.rental_option;
        return {
          ['주문일(시간)']: moment(item.created_at * 1000).format(
            'YYYY-MM-DD(HH:mm:ss)',
          ),
          ['주문번호(신청일시)']: item?.shop_order_id,
          ['품목별주문번호']: item?.rental_id,
          ['진행회차']: `${item?.rental_payment_info?.length}회차`,
          ['택배사(송장번호)']: item?.invoice_number
            ? `${item?.delivery_company}(${item?.invoice_number})`
            : '',
          ['받는분핸드폰']: delivery_address.recipient_phone,
          ['받는분총주소']: `${delivery_address.recipient_address}, ${delivery_address.recipient_address_detail}`,
          ['수량']: `${item?.num}`,
          ['상품명']: item?.product?.product_name ?? '',
          ['상품옵션정보']:
            item?.product?.product_option
              ?.map((optionItem) => {
                const optionNames = optionItem?.option
                  ?.map((opt) => opt?.option_name || '-')
                  ?.join(', ');
                return `- ${optionItem.option_title}: ${optionNames}`;
              })
              .join(' / ') ?? '-',
          ['렌탈선택옵션']: [
            rental_option?.advance_price
              ? `선납입금: ${rental_option.advance_price.option_name} / ${RENTAL_TYPE_CONSTANT[rental_option.advance_price.option_type].title} / ${stringToMoneyFormat(rental_option.advance_price.option_price)}원`
              : null,
            rental_option?.management
              ? `관리유형: ${rental_option.management.option_name} / ${RENTAL_TYPE_CONSTANT[rental_option.management.option_type].title} / ${stringToMoneyFormat(rental_option.management.option_price)}원`
              : null,
            rental_option?.additional_cost
              ? `추가비용: ${rental_option.additional_cost.option_name} / ${RENTAL_TYPE_CONSTANT[rental_option.additional_cost.option_type].title} / ${stringToMoneyFormat(rental_option.additional_cost.option_price)}원`
              : null,
          ]
            .filter(Boolean)
            .join('\r\n'),
          ['판매자']: item?.shop_information?.shop_information_name ?? '',
          ['구매자']: item?.user.user_name,
          ['결제금액']: stringToMoneyFormat(
            item?.rental_payment_info?.[0]?.price,
          ),
          ['월결제금액']: item?.rental_price,
          ['결제수단']: PAYMENT_METHOD_CONSTANT[item?.payment_method].title,
          ['계약기간']: item?.contract_date
            ? `${item?.contract_date} 개월`
            : '-',
          ['의무계약기간']: item?.mandatory_usage_date
            ? `${item?.mandatory_usage_date} 개월`
            : '-',
          ['렌탈시작기간']: item?.rental_start_date
            ? moment(item.rental_start_date * 1000).format(
                'YYYY-MM-DD (HH:mm:ss)',
              )
            : '진행전',
          ['렌탈종료기간']: item?.rental_end_date
            ? moment(item.rental_end_date * 1000).format(
                'YYYY-MM-DD (HH:mm:ss)',
              )
            : '진행전',
          ['주문상태']: PURCHASE_STATUS[item?.purchase_status].title,
          ['배송상태']: DELIVERY_STATUS[item?.delivery_status].title,
          ['배송메모']: delivery_address.request,
        };
      });

      const ws = XLSX.utils.json_to_sheet(xlsxData);
      Object.keys(ws).forEach((key) => {
        if (
          ws[key].v &&
          typeof ws[key].v === 'string' &&
          ws[key].v.includes('\n')
        ) {
          ws[key].s = { alignment: { wrapText: true } }; // 여기서 wrapText 적용
        }
      });
      const wb = XLSX.utils.book_new();

      XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
      setTimeout(() => {
        XLSX.writeFile(
          wb,
          `주문 데이터 내보내기_${moment().format('YYYYMMDD_HHmmss')}.xlsx`,
        );
        setLoading(false);
      }, 1200);
    } catch (e) {
      setLoading(false);
      console.log(e);
      MessageAlert.error('엑셀을 다운로드 할 수 없습니다.');
    }
  };

  /**--------------------
   * 파일 업로드
   * --------------------
   */
  const handleFile = async (file) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      const data = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: 'array' });
      const wsname = workbook.SheetNames[0];
      const ws = workbook.Sheets[wsname];
      const dataParse = XLSX.utils.sheet_to_json(ws);

      // 데이터 추출
      const result = dataParse.map((item) => {
        const newItem = {
          shop_order_id: item['주문번호'],
          rental_id: item['품목별주문번호'],
          invoice_number: item['운송장번호'],
          num: item['수량'],
          delivery_company: item['배송업체번호'],
        };
        return newItem;
      });
      setUploadData(result);
      MessageAlert.success(`파일이 업로드 되었습니다.`);
    };

    reader.onerror = (error) => {
      MessageAlert.error('파일을 읽는 중 오류가 발생했습니다.');
      console.error('File reading error:', error);
    };

    reader.readAsArrayBuffer(file); // 실제 파일 객체를 읽음
  };

  /**--------------------
   * 엑셀 양식 다운로드
   * --------------------
   */
  const handleGetExcelTemplate = (type, data) => {
    try {
      const xlsxData = data.map((_item) => {
        return {
          ['주문번호']: '',
          ['품목별주문번호']: '',
          ['운송장번호']: '',
          ['수량']: '',
          ['배송업체번호']: currentTab === 1 ? currentDeliveryCompany : null,
        };
      });

      const ws = XLSX.utils.json_to_sheet(xlsxData);
      const wb = XLSX.utils.book_new();

      XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
      XLSX.writeFile(
        wb,
        `운송장_일괄등록_양식_${moment().format('YYYYMMDD_HHmmss')}.xlsx`,
      );
    } catch (e) {
      console.log(e);
      MessageAlert.error('엑셀을 다운로드 할 수 없습니다.');
    }
  };

  /**--------------------
   * 운송장 일괄저장=> 엑셀이 등록된 경우에만 처리가능
   * --------------------
   */
  const handleSaveInvoice = async () => {
    setLoading(true);
    try {
      /* 예외처리 */
      // 빈값체크
      if (!uploadData || uploadData.length < 1) {
        MessageAlert.warning('엑셀파일을 등록해주세요');
        setLoading(false);
        return;
      }

      // 양식체크
      if (
        !uploadData[0].shop_order_id ||
        !uploadData[0].rental_id ||
        !uploadData[0].invoice_number ||
        !uploadData[0].delivery_company
      ) {
        MessageAlert.warning('업로드된 파일의 양식을 확인해주세요');
        setLoading(false);
        return;
      }
      // 데이터 만들기
      const dc = Object.keys(DeliveryCompany);
      const newData = uploadData.map((item) => {
        const findKey = dc.find(
          (key) => DeliveryCompany[key].shippingCode === item.delivery_company,
        );
        return {
          rental_id: item.rental_id,
          shop_order_id: item.shop_order_id,
          invoice_number: `${item.invoice_number}`,
          delivery_company: DeliveryCompany[findKey].value,
        };
      });

      const { status, message, data } = await bulkUpdateRental(newData);

      if (status !== 200) throw { message };
      setTimeout(() => {
        MessageAlert.success('저장되었습니다');
        onReload();
        setLoading(false);
      }, 1000);
      // API
    } catch (e) {
      setLoading(false);
      console.log('[OrderData][handleSaveInvoice] Error: ', e);
      MessageAlert.error(e.message);
    }
  };

  /**--------------------
   * 입금확인 함수
   * --------------------
   */
  const handleDepositCheck = async (key, value) => {
    if (!selectedList?.length) {
      MessageAlert.warning('처리할 항목을 선택해주세요');
      return;
    }

    try {
      // // 입금상태가 PENDING인경우만 상태변경처리
      // let deliveryStatus;
      // if (type === 'PENDING') {
      //   deliveryStatus = 'PREPARING';
      // } else if (type === 'PREPARING') {
      //   deliveryStatus = 'SHIPPING';
      // } else if (type === 'SHIPPING') {
      //   deliveryStatus = 'DELIVERED';
      // }
      // const newData = selectedList.map((item) => ({
      //   parcel_service_id: item.parcel_service_id,
      //   delivery_status: 'PREPARING',
      // }));
      let success = 0;
      const newData = selectedList.map((item) => ({
        rental_id: item.rental_id,
        [key]: value,
      }));
      for (const data of newData) {
        const { status, message } = await updateStatusRental(data);
        if (status === 200) ++success;
      }
      if (success) {
        onReload();
        MessageAlert.info(
          `${success}건 ${DELIVERY_STATUS?.[value]?.title} 상태로 변경되었습니다.`,
        );
      }
    } catch (e) {
      MessageAlert.error(e.message);
    }
  };

  /**--------------------
   * 일괄수정 함수
   * --------------------
   */
  const handleBulkUpdateRental = async (isModalData = true, data) => {
    const updateValue = isModalData ? dateModal : data;
    try {
      if (!window.confirm(`${updateValue.title}을 변경하시겠습니까?`)) return;
      const newData = [
        {
          rental_id: updateValue.rental_id,
          [updateValue.key]: updateValue.value,
        },
      ];
      const { status, message } = await bulkUpdateRental(newData);

      if (status !== 200) throw { message };
      MessageAlert.success('수정 되었습니다');
      setTimeout(() => {
        onReload();
        setDateModal(null);
      }, 1000);
    } catch (e) {
      MessageAlert.error(e.message);
    }
  };
  /* ===== HOOKS ===== */
  /*  */
  useEffect(() => {
    registerDeliveryInfoModal &&
      setRegisterDeliveryInfo({
        delivery_company: registerDeliveryInfoModal.delivery_company,
        invoice_number: registerDeliveryInfoModal.invoice_number,
      });
  }, [registerDeliveryInfoModal]);

  useEffect(() => {
    if (detailModal) {
      setDetailModal(
        orderList?.find((i) => i.rental_id === detailModal.rental_id),
      );
    }
  }, [orderList]);
  /* ===== RENDER ===== */
  // JSX: start
  return (
    <>
      <Spin spinning={loading}>
        <Content
          maxWidth={'100%'}
          width={1400}
          height={'100%'}
          minHeight={490}
          borderRadius={4}
          padding={20}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 10,
            }}
          >
            <Title size={5}>
              검색 결과 총 <span>{orderList?.length}</span>건
            </Title>
            <div>
              {/* 배송준비처리 */}
              {Object.values(DELIVERY_STATUS).map((item, index) => (
                <Button
                  size="default"
                  style={{ marginLeft: 5 }}
                  // type="primary"
                  onClick={() =>
                    handleDepositCheck('delivery_status', item.value)
                  }
                >
                  {item.title} 처리
                </Button>
              ))}
              {/* {type !== 'DELIVERED' && (
                <Button
                  size="default"
                  style={{ marginLeft: 5 }}
                  type="primary"
                  disabled={selectedList?.length > 0 ? false : true}
                  onClick={handleDepositCheck}
                >
                  {type === 'PENDING'
                    ? '배송준비중'
                    : type === 'PREPARING'
                    ? '배송중'
                    : type === 'SHIPPING'
                    ? '배송완료'
                    : type === 'ON_HOLD'
                    ? '배송중'
                    : ''}{' '}
                  처리
                </Button>
              )} */}
              <Button
                size="default"
                style={{ marginLeft: 5 }}
                onClick={() => handleExportOrderData(orderList)}
              >
                데이터 엑셀 다운로드
              </Button>
              {type === 'PENDING' ||
                (type === 'PREPARING' && (
                  <>
                    <Button
                      size="default"
                      style={{ marginLeft: 5 }}
                      onClick={handleExportDeliveryData}
                    >
                      택배정보 내보내기
                    </Button>
                    {/* <Button
                      size="default"
                      style={{ marginLeft: 5 }}
                      // type="primary"
                      disabled={selectedList?.length > 0 ? false : true}
                      onClick={handleSaveInvoice}
                    >
                      운송장 일괄등록
                    </Button> */}
                  </>
                ))}
            </div>
          </div>
          <div
            style={{
              overflowX: 'auto',
            }}
          >
            <div
              style={
                {
                  // width: 1650,
                }
              }
            >
              {/* 
            NOTE: 
            deliver => 일반 상품
            subscription && is_active : Y인것은 구독중인 상품
            */}
              <Table
                size="small"
                // data={orderList}
                dataSource={orderList.map((item) => ({
                  ...item,
                  key: item.rental_id,
                }))}
                pagination={false}
                bordered
                // isChecked
                rowSelection={{
                  type: 'checkbox',
                  selectedRowKeys: selectedList?.map((item) => item.key),
                  onChange: (_selectedRowKeys, _selectedRows) => {
                    setSelectedList(_selectedRows);
                  },
                }}
                scroll={{ x: 'max-content' }}
                columns={columns}
                // rental_updated_at => 다음결제 예정일
                expandable={{
                  // defaultExpandedRowKeys: [1],
                  // expandedRowKeys: [1],
                  // rowExpandable: (record) => record.name === 'Not Expandable',
                  expandedRowRender: (currentRow) => {
                    return (
                      <Table
                        // dataSource={result}
                        dataSource={
                          currentRow?.rental_payment_info
                            ?.map((item) => ({
                              ...item,
                              key: `${currentRow?.rental_id}-${item.rental_payment_id}`,
                            }))
                            .sort(
                              (a, b) =>
                                b.rental_payment_id - a.rental_payment_id,
                            ) ?? []
                        }
                        pagination={false}
                        columns={expandableColumns}
                        // rowSelection={{
                        //   type: 'checkbox',
                        //   onSelect: (record, selected) => {
                        //     setSelectedList((prev) => {
                        //       const exists = prev.some(
                        //         (item) => item.key === record.key
                        //       );
                        //       if (selected && !exists) {
                        //         return [...prev, record];
                        //       } else if (!selected) {
                        //         return prev.filter(
                        //           (item) => item.key !== record.key
                        //         );
                        //       }
                        //       return prev;
                        //     });

                        //     setSelectedRowKeys((prev) => {
                        //       if (selected) {
                        //         return [...prev, record.key];
                        //       } else {
                        //         return prev.filter((key) => key !== record.key);
                        //       }
                        //     });
                        //   },

                        //   // 전체 선택/해제
                        //   onSelectAll: (selected, selectedRows, changeRows) => {
                        //     const changeKeys = changeRows.map((row) => row.key);

                        //     if (selected) {
                        //       setSelectedList((prev) => {
                        //         const existingKeys = new Set(
                        //           prev.map((item) => item.key)
                        //         );
                        //         const newItems = changeRows.filter(
                        //           (row) => !existingKeys.has(row.key)
                        //         );
                        //         return [...prev, ...newItems];
                        //       });

                        //       setSelectedRowKeys((prev) => {
                        //         const existingSet = new Set(prev);
                        //         const newKeys = changeKeys.filter(
                        //           (key) => !existingSet.has(key)
                        //         );
                        //         return [...prev, ...newKeys];
                        //       });
                        //     } else {
                        //       setSelectedList((prev) =>
                        //         prev.filter(
                        //           (item) => !changeKeys.includes(item.key)
                        //         )
                        //       );
                        //       setSelectedRowKeys((prev) =>
                        //         prev.filter((key) => !changeKeys.includes(key))
                        //       );
                        //     }
                        //   },
                        // }}
                        scroll={{ x: 'max-content' }}
                      />
                    );
                  },
                }}
              />

              {/* /Table */}
            </div>
          </div>
          {/* <Pagination
            total={orderList.length}
            //total={users?.length}
            align="center"
            style={{
              marginTop: 40,
              display: 'flex',
              justifyContent: 'center',
            }}
          /> */}
        </Content>

        {/* ===== 운송장번호 일괄처리 ===== */}
        {type !== 'PENDING' ? (
          <Content
            maxWidth={'100%'}
            width={1400}
            height={'100%'}
            minHeight={490}
            borderRadius={4}
            padding={20}
            style={{ marginTop: 25 }}
          >
            <Flex align="center" justify="space-between">
              <Title>엑셀 일괄배송 처리</Title>
              <Button
                size="default"
                style={{ marginLeft: 5 }}
                type="primary"
                // disabled={selectedList?.length > 0 ? false : true}
                onClick={handleSaveInvoice}
              >
                운송장 일괄등록
              </Button>
            </Flex>

            <Tabs
              defaultActiveKey="1"
              items={[
                // === 단일 배송사 === //
                {
                  key: 1,
                  label: '엑셀 하나에 배송사 1개',
                },

                // === 여러 배송사 === //
                {
                  key: 2,
                  label: '엑셀 하나에 배송사 여러개',
                },
              ]}
              onChange={(key) => {
                setCurrentTab(key);
              }}
            />
            <Descriptions
              column={2}
              // size="small"
              bordered
              labelStyle={{
                width: 125,
                color: '#111',
                fontWeight: '500',
                background: '#f4f4f4',
              }}
              style={{
                borderTop: '2px solid #d1d1d1',
                borderRadius: 5,
                marginTop: 5,
              }}
              items={[
                /* === Line 1 === */
                ...(currentTab === 1
                  ? [
                      {
                        key: 'shop_order_id',
                        label: '배송사',
                        span: 2,
                        children: (
                          <Select
                            style={{ width: 200 }}
                            value={currentDeliveryCompany}
                            onChange={(value) =>
                              setCurrentDeliveryCompany(value)
                            }
                          >
                            {deliveryCompanyList.map((item) => (
                              <Select.Option
                                key={`dcl_${item}`}
                                value={DeliveryCompany[item].shippingCode}
                              >
                                {DeliveryCompany[item].label}:{' '}
                                {DeliveryCompany[item].shippingCode}
                              </Select.Option>
                            ))}
                          </Select>
                        ),
                      },
                    ]
                  : []),

                /* === Line 2 === */
                {
                  key: 'shop_order_id',
                  label: '엑셀파일등록',
                  // span: 2,
                  children: (
                    <Upload
                      // onChange={handleFile}
                      onRemove={(_file) => setUploadData(null)}
                      beforeUpload={(file) => {
                        handleFile(file);
                        return false;
                      }}
                      maxCount={1}
                    >
                      <Button icon={<UploadOutlined />}>
                        엑셀파일을 양식에 맞추어 업로드해주세요
                      </Button>
                    </Upload>
                  ),
                },
                {
                  key: 'payment_serial_number',
                  label: '양식다운로드',
                  width: 200,
                  children: (
                    <Button
                      onClick={() =>
                        handleGetExcelTemplate('single', [1, 2, 3, 4, 5])
                      }
                    >
                      엑셀 양식 다운로드
                    </Button>
                  ),
                },
                /* === Line 3 === */
                {
                  key: 'shop_order_id',
                  label: '처리결과',
                  span: 2,
                  children: (
                    <div
                      style={{
                        width: '100%',
                        background: '#fafafa',
                        border: '1px solid #f1f1f1',
                        padding: 5,
                        height: 120,
                        overflowX: 'auto',
                      }}
                    >
                      <ul>
                        {uploadData && uploadData.length > 0 ? (
                          uploadData.map((item, key) => (
                            <li
                              key={`asdiwm_${key}`}
                              style={{
                                borderBottom: '1px solid #f1f1f1',
                                color: '#5a5a5a',
                                fontWeight: '300',
                              }}
                            >
                              {JSON.stringify(item)}
                            </li>
                          ))
                        ) : (
                          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                        )}
                      </ul>
                    </div>
                  ),
                },
              ]}
            />
          </Content>
        ) : null}
      </Spin>

      {/* ===== 택배정보 등록 모달 start ===== */}
      <ModalLayout
        title="택배사/송장번호 등록"
        width={530}
        closable={true}
        open={registerDeliveryInfoModal !== null}
        onCancel={() => setRegisterDeliveryInfoModal(null)}
        footer={
          <>
            <Button
              type="primary"
              size="large"
              onClick={handleSaveDeliveryInfo}
            >
              등록
            </Button>
          </>
        }
      >
        <Divider style={{ margin: '15px 0' }} />
        {registerDeliveryInfoModal && (
          <>
            {/* === 택배사/송장번호 입력폼 start === */}
            <div>
              <Space.Compact size="large" style={{ width: '100%' }}>
                <Select
                  style={{ width: 170 }}
                  placeholder="택배사"
                  options={Object.keys(DeliveryCompany).map((item) => ({
                    value: DeliveryCompany[item].value,
                    label: DeliveryCompany[item].label,
                  }))}
                  value={registerDeliveryInfo?.delivery_company}
                  onChange={(e) =>
                    setRegisterDeliveryInfo((p) => ({
                      ...p,
                      delivery_company: e,
                    }))
                  }
                />
                <Input
                  placeholder="송장번호를 입력해주세요"
                  style={{ width: 'calc(100% - 170px)' }}
                  value={registerDeliveryInfo?.invoice_number}
                  onChange={(e) =>
                    setRegisterDeliveryInfo((p) => ({
                      ...p,
                      invoice_number: e.target.value,
                    }))
                  }
                />
              </Space.Compact>
            </div>
            {/* === 택배사/송장번호 입력폼 end === */}

            <Divider style={{ margin: '15px 0' }} />

            {/* === 확인정보 start === */}
            <Title size={5}>입력정보 확인</Title>
            <div style={{ padding: 3.5, lineHeight: 1.85 }}>
              {(() => {
                /* Variables */
                const {
                  recipient_postcode,
                  recipient_address,
                  recipient_address_detail,
                } = registerDeliveryInfoModal.shop_order.delivery_address;
                const { delivery_company, invoice_number } =
                  registerDeliveryInfo;

                /* Render */
                return (
                  <p>
                    주소:
                    {` [${recipient_postcode}] ${recipient_address}(${recipient_address_detail})`}
                    <br />
                    택배사(코드): {DeliveryCompany[delivery_company]?.label}(
                    {DeliveryCompany[delivery_company]?.value})
                    <br />
                    송장번호: {invoice_number}
                  </p>
                );
              })()}
            </div>
            {/* === 확인정보 end === */}
          </>
        )}
        <Divider style={{ margin: '15px 0' }} />
      </ModalLayout>
      {/* ===== 택배정보 등록 모달: end ===== */}

      {/* ===== 상세모달: start ===== */}
      <ModalLayout
        type="drawer"
        placement="right"
        title="주문 상세 보기"
        width={1350}
        maxWidth={1350}
        closable={true}
        open={detailModal !== null}
        onCancel={() => setDetailModal(null)}
      >
        {detailModal && (
          <RentalDetail
            orderId={detailModal.shop_order_id}
            orderDetailData={detailModal}
            onCancel={onCancel}
            type={type}
            modalType={'shipping'}
            rentalId={detailModal.rental_id}
            onReload={onReload}
          />
        )}
      </ModalLayout>
      <ModalLayout
        title={dateModal?.title}
        width={530}
        closable={true}
        open={dateModal !== null}
        onCancel={() => setDateModal(null)}
        footer={
          <>
            <Button
              type="primary"
              size="large"
              onClick={handleBulkUpdateRental}
            >
              수정
            </Button>
          </>
        }
      >
        <DatePicker
          style={{ width: '100%' }}
          value={dayjs.unix(dateModal?.value)}
          onChange={(e) => {
            setDateModal((p) => ({
              ...p,
              value: dayjs(e).unix(),
            }));
          }}
          format={'YYYY-MM-DD HH:mm:ss'}
          showTime
        />
      </ModalLayout>
      {/* ===== 상세모달: end ===== */}
    </>
  ); // JSX: end
};

RentalData.defaultProps = {
  type: 'SHIPPING',
  orderList: [],
  onSaveDeliveryInfo: () => {},
  onReload: () => {},
};
export default RentalData;
